import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, ChevronLeft, ChevronRight, Edit, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { userService } from '@/services/userService'
import { branchService } from '@/services/branchService'
import type { BranchEntity, UserListItem, UserListResponse } from '@/api/types'

export const UsersList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const branchId = user?.selectedBranch?.branchId ?? 0

  const [rows, setRows] = useState<UserListItem[]>([])
  const [employees, setEmployees] = useState<Record<number, string>>({})
  const [roles, setRoles] = useState<Record<number, string>>({})
  const [branches, setBranches] = useState<Record<number, string>>({})
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  // Load main list
  useEffect(() => {
    if (!branchId) return

    const load = async () => {
      try {
        setLoading(true)
        const res: UserListResponse = await userService.getAllUsers(branchId, pageNumber, pageSize)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
        const tp = res.totalRecords ? Math.ceil(res.totalRecords / res.pageSize) : 0
        setTotalPages(tp)
      } catch (error: any) {
        const msg = error?.response?.data?.message || t('userPage.fetchFailed')
        toast.error(msg)
        setRows([])
        setTotalRecords(0)
        setTotalPages(0)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [branchId, pageNumber, pageSize])

  // Load lookups for employee name, role name, branch name
  useEffect(() => {
    if (!branchId) return

    const loadLookups = async () => {
      try {
        const [empList, roleList, branchList] = await Promise.all([
          userService.getEmployees(branchId),
          userService.getRoles(branchId),
          branchService.getAllBranches(),
        ])

        const empMap: Record<number, string> = {}
        empList.forEach((e) => {
          if (e.id) empMap[e.id] = e.fullName
        })
        setEmployees(empMap)

        const roleMap: Record<number, string> = {}
        roleList.forEach((r) => {
          if (r.id) roleMap[r.id] = r.name
        })
        setRoles(roleMap)

        const branchMap: Record<number, string> = {}
        ;(branchList || []).forEach((b: BranchEntity) => {
          if (b.id) {
            branchMap[b.id] = b.nameEn || b.shortName || `Branch ${b.id}`
          }
        })
        setBranches(branchMap)
      } catch (error) {
        console.error('Failed to load user lookups:', error)
      }
    }

    loadLookups()
  }, [branchId])

  const resolvedRows = useMemo<UserListItem[]>(() => {
    return rows.map((r) => ({
      ...r,
      fullName: employees[r.empId] ?? r.fullName,
      roleName: roles[r.roleId] ?? r.roleName,
      branchName: branches[r.branchId] ?? r.branchName,
    }))
  }, [rows, employees, roles, branches])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return resolvedRows
    const q = searchQuery.toLowerCase()
    const activeLabel = t('userPage.statusActive').toLowerCase()
    const inactiveLabel = t('userPage.statusInactive').toLowerCase()
    return resolvedRows.filter((u) => {
      const name = (u.fullName || '').toLowerCase()
      const role = (u.roleName || '').toLowerCase()
      const branch = (u.branchName || '').toLowerCase()
      const statusStr =
        u.isActive === 1 || u.isActive === true ? activeLabel : inactiveLabel
      return (
        name.includes(q) ||
        role.includes(q) ||
        branch.includes(q) ||
        statusStr.includes(q) ||
        (u.status || '').toString().toLowerCase().includes(q)
      )
    })
  }, [resolvedRows, searchQuery, t])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber((p) => p - 1)
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber((p) => p + 1)
  }

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)
  const pageTotal = Math.max(1, totalPages || 1)

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('userPage.pageTitle')} description={t('userPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('userPage.branchRequiredTitle')}
              description={t('userPage.branchRequiredDescriptionList')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('userPage.pageTitle')}
        description={t('userPage.pageDescription')}
        action={{
          label: t('userPage.addUser'),
          onClick: () => navigate('/security/users/new'),
          icon: Plus,
        }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('userPage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('userPage.emptyNoUsers')}
                description={searchQuery ? t('userPage.emptySearchHint') : t('userPage.emptyNoCreated')}
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">
                      {t('userPage.colFullName')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">
                      {t('userPage.colRole')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('userPage.colStatus')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">
                      {t('userPage.colBranch')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">
                      {t('userPage.colCreatedDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-24">
                      {t('userPage.colAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow key={row.id} className="border-b hover:bg-primary/5">
                      <TableCell className="px-4 py-2 align-middle">
                        {row.fullName || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-2 align-middle">
                        {row.roleName || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <Badge
                          variant={row.isActive === 1 || row.isActive === true ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {row.isActive === 1 || row.isActive === true
                            ? t('userPage.statusActive')
                            : t('userPage.statusInactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2 align-middle">
                        {row.branchName || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-2 align-middle">
                        {formatDate(row.createdDate)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary hover:bg-primary/10"
                          title={t('userPage.editTitle')}
                          onClick={() => navigate(`/security/users/${row.id}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t('userPage.pageSize')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('userPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('userPage.previous')}
                  </Button>
                  <span className="text-sm">
                    {t('userPage.pageOf', { page: pageNumber, totalPages: pageTotal })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={pageNumber >= totalPages || totalPages === 0}
                  >
                    {t('userPage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
